import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Descriptions,
  Drawer,
  Form,
  Grid,
  Input,
  Menu,
  message,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Transfer,
} from "antd";
import styled from "styled-components";
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useFirestore } from "react-redux-firebase";
import { useSelector } from "react-redux";
import * as dayjs from "dayjs";
import axios from "axios";
import utils from "utils";
import Flex from "components/shared-components/Flex";
import EllipsisDropdown from "../../../../components/shared-components/EllipsisDropdown";
import { FIRESTORE_SUPPLIERS_TABLE } from "../../../../constants/FirestoreConstant";
import {
  SUPPLIER_STATUS_ACTIVE,
  SUPPLIER_STATUS_CREATED,
  SUPPLIER_STATUS_INACTIVE,
  SUPPLIER_STATUS_INVITED,
} from "../../../../constants/SupplierConstants";
import { useSuppliers } from "../../../../data/hooks/useSuppliers";
import { getFirebaseIDToken } from "../../../../functions/firebase/firebase";
import { getExactSuppliers, getExactSupplierItems } from "../../../../urls";

const { Option } = Select;

const Suppliers = () => {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const firestore = useFirestore();
  const { suppliers, loading } = useSuppliers();
  const products = useSelector((state) => state.firestoreReducer.ordered.products);
  const categories = useSelector((state) => state.firestoreReducer.ordered.categories);
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const [filteredList, setFilteredList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [exactSuppliers, setExactSuppliers] = useState([]);
  const [exactSuppliersLoading, setExactSuppliersLoading] = useState(false);
  const [addModalProductIds, setAddModalProductIds] = useState([]);
  const [editModalProductIds, setEditModalProductIds] = useState([]);

  useEffect(() => {
    if (suppliers) {
      setFilteredList(suppliers);
      onSearch(searchText, suppliers);
    }
  }, [suppliers]);

  const getSupplierStatusColor = (status) => {
    switch (status) {
      case SUPPLIER_STATUS_CREATED:
        return "yellow";
      case SUPPLIER_STATUS_INVITED:
        return "blue";
      case SUPPLIER_STATUS_ACTIVE:
        return "green";
      case SUPPLIER_STATUS_INACTIVE:
        return "red";
      default:
        return "default";
    }
  };

  const onSearch = (value, suppliersList) => {
    setSearchText(value);
    const data = utils.wildCardSearch(suppliersList || filteredList, value);
    setFilteredList(data);
  };

  const openAddModal = async () => {
    setAddModalVisible(true);
    // Fetch Exact suppliers
    setExactSuppliersLoading(true);
    try {
      const token = await getFirebaseIDToken();
      const response = await axios.get(getExactSuppliers, { headers: { Authorization: token } });
      if (response.data && Array.isArray(response.data)) {
        // Filter out suppliers that are already in use
        const usedExactGuids =
          suppliers?.filter((supplier) => supplier.exactGuid).map((supplier) => supplier.exactGuid) || [];

        const availableSuppliers = response.data.filter((exactSupplier) => !usedExactGuids.includes(exactSupplier.Id));

        setExactSuppliers(availableSuppliers);
      }
    } catch (error) {
      console.error("Error fetching Exact suppliers:", error);
      messageApi.error(t("notifications.error_fetching_exact_suppliers"));
    } finally {
      setExactSuppliersLoading(false);
    }
  };

  const closeAddModal = () => {
    setAddModalVisible(false);
    addForm.resetFields();
    setSubmitLoading(false);
    setAddModalProductIds([]);
  };

  const openEditModal = (supplier) => {
    setSelectedSupplier(supplier);
    const productIds = supplier.productIds || [];
    editForm.setFieldsValue({
      name: supplier.name,
      status: supplier.status,
      currency: supplier.currency || "USD",
      productIds: productIds,
    });
    setEditModalProductIds(productIds);
    setEditModalVisible(true);
  };

  const closeEditModal = () => {
    setEditModalVisible(false);
    setSelectedSupplier(null);
    editForm.resetFields();
    setSubmitLoading(false);
    setEditModalProductIds([]);
  };

  const showDrawer = (supplier) => {
    setSelectedSupplier(supplier);
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setSelectedSupplier(null);
  };

  // Mobile card component
  const SupplierCard = ({ supplier }) => {
    return (
      <MobileSupplierCard onClick={() => showDrawer(supplier)}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <h4 style={{ margin: 0, marginBottom: 4, fontSize: 16, fontWeight: 600 }}>{supplier.name}</h4>
            <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>{supplier.email}</div>
            <div style={{ fontSize: 12, color: "#888" }}>{supplier.productIds?.length || 0} producten</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
            <Tag color={getSupplierStatusColor(supplier.status)}>{t(`supplier.${supplier.status}`)}</Tag>
            <Tag>{supplier.currency || "USD"}</Tag>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 12, color: "#888" }}>
            {supplier.lastLogin ? dayjs.unix(supplier.lastLogin).format("DD-MM-YYYY HH:mm") : t("never_logged_in")}
          </div>
          <EllipsisDropdown
            menu={
              <Menu>
                <Menu.Item
                  onClick={(e) => {
                    e.domEvent.stopPropagation();
                    openEditModal(supplier);
                  }}
                >
                  <Flex alignItems="center">
                    <EditOutlined />
                    <span style={{ marginLeft: 4 }}>{t("edit")}</span>
                  </Flex>
                </Menu.Item>
                <Menu.Item
                  onClick={(e) => {
                    e.domEvent.stopPropagation();
                    handleDeleteSupplier(supplier);
                  }}
                  danger
                >
                  <Flex alignItems="center">
                    <DeleteOutlined />
                    <span style={{ marginLeft: 4 }}>{t("delete")}</span>
                  </Flex>
                </Menu.Item>
              </Menu>
            }
          />
        </div>
      </MobileSupplierCard>
    );
  };

  // Helper function to create Transfer data source with category grouping
  const getTransferDataSource = () => {
    if (!products || products.length === 0) return [];

    // Create a map to group products by category
    const productsByCategory = {};
    const productsWithoutCategory = [];

    products.forEach((product) => {
      if (product.category) {
        if (!productsByCategory[product.category]) {
          productsByCategory[product.category] = [];
        }
        productsByCategory[product.category].push(product);
      } else {
        productsWithoutCategory.push(product);
      }
    });

    // Sort products within each category by product code
    Object.keys(productsByCategory).forEach((categoryId) => {
      productsByCategory[categoryId].sort((a, b) => {
        const codeA = a.code || "";
        const codeB = b.code || "";
        return codeA.localeCompare(codeB);
      });
    });

    // Sort products without category by product code
    productsWithoutCategory.sort((a, b) => {
      const codeA = a.code || "";
      const codeB = b.code || "";
      return codeA.localeCompare(codeB);
    });

    // Build the flat data array for Transfer
    const dataSource = [];

    // Get category names and sort them alphabetically
    const categoryIds = Object.keys(productsByCategory).sort((a, b) => {
      const categoryA = categories?.find(
        (cat) => cat.categoryId === a || cat.subcategories?.some((sub) => sub.subcategoryId === a),
      );
      const categoryB = categories?.find(
        (cat) => cat.categoryId === b || cat.subcategories?.some((sub) => sub.subcategoryId === b),
      );

      const nameA = getCategoryName(a, categoryA) || "";
      const nameB = getCategoryName(b, categoryB) || "";

      return nameA.localeCompare(nameB);
    });

    // Add products grouped by category
    categoryIds.forEach((categoryId) => {
      const category = categories?.find(
        (cat) => cat.categoryId === categoryId || cat.subcategories?.some((sub) => sub.subcategoryId === categoryId),
      );

      const categoryName = getCategoryName(categoryId, category);

      productsByCategory[categoryId].forEach((product) => {
        dataSource.push({
          key: product.id,
          title: `${product.name} ${product.code ? `(${product.code})` : ""}`,
          categoryName: categoryName || t("uncategorized"),
          code: product.code || "",
        });
      });
    });

    // Add products without category at the end
    productsWithoutCategory.forEach((product) => {
      dataSource.push({
        key: product.id,
        title: `${product.name} ${product.code ? `(${product.code})` : ""}`,
        categoryName: t("uncategorized"),
        code: product.code || "",
      });
    });

    return dataSource;
  };

  // Render function for Transfer items to show category
  const renderTransferItem = (item) => {
    return {
      label: (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span>{item.title}</span>
          <span style={{ fontSize: "12px", color: "#999" }}>{item.categoryName}</span>
        </div>
      ),
      value: item.title,
    };
  };

  // Helper function to get category name from category ID
  const getCategoryName = (categoryId, category) => {
    if (!category) return categoryId;

    // Check if it's a main category
    if (category.categoryId === categoryId) {
      return category.name;
    }

    // Check if it's a subcategory
    const subcategory = category.subcategories?.find((sub) => sub.subcategoryId === categoryId);
    if (subcategory) {
      return `${category.name} > ${subcategory.name}`;
    }

    return categoryId;
  };

  const handleAddSupplier = () => {
    addForm.validateFields().then(async (values) => {
      setSubmitLoading(true);
      try {
        const supplierData = {
          name: values.name.trim(),
          email: values.email.trim(),
          status: SUPPLIER_STATUS_CREATED,
          currency: values.currency || "USD",
          productIds: values.productIds || [],
          createdAt: dayjs().unix(),
        };

        // Add Exact supplier data if selected
        if (values.exactSupplierId) {
          const selectedExactSupplier = exactSuppliers.find((supplier) => supplier.Id === values.exactSupplierId);
          if (selectedExactSupplier) {
            supplierData.exactGuid = selectedExactSupplier.Id;
          }
        }

        await firestore.collection(FIRESTORE_SUPPLIERS_TABLE).add(supplierData);
        messageApi.success(t("notifications.supplier_added_successful"));
        closeAddModal();
      } catch (error) {
        console.error("Error adding supplier:", error);
        messageApi.error(t("notifications.supplier_added_failed"));
        setSubmitLoading(false);
      }
    });
  };

  const handleEditSupplier = () => {
    editForm.validateFields().then(async (values) => {
      setSubmitLoading(true);
      try {
        await firestore
          .collection(FIRESTORE_SUPPLIERS_TABLE)
          .doc(selectedSupplier.id)
          .update({
            name: values.name.trim(),
            status: values.status,
            currency: values.currency || "USD",
            productIds: values.productIds || [],
            updatedAt: dayjs().unix(),
          });
        messageApi.success(t("notifications.supplier_updated_successful"));
        closeEditModal();
      } catch (error) {
        console.error("Error updating supplier:", error);
        messageApi.error(t("notifications.supplier_updated_failed"));
        setSubmitLoading(false);
      }
    });
  };

  const handleDeleteSupplier = (supplier) => {
    Modal.confirm({
      title: t("delete_supplier"),
      content: t("delete_supplier_confirm", { name: supplier.name }),
      okText: t("delete"),
      okType: "danger",
      cancelText: t("cancel"),
      onOk: async () => {
        try {
          await firestore.collection(FIRESTORE_SUPPLIERS_TABLE).doc(supplier.id).delete();
          messageApi.success(t("notifications.supplier_deleted_successful"));
          if (drawerVisible && selectedSupplier?.id === supplier.id) {
            closeDrawer();
          }
        } catch (error) {
          console.error("Error deleting supplier:", error);
          messageApi.error(t("notifications.supplier_deleted_failed"));
        }
      },
    });
  };

  const tableColumns = [
    {
      title: t("name"),
      dataIndex: "name",
      render: (_, supplier) => (
        <a
          onClick={(e) => {
            e.preventDefault();
            showDrawer(supplier);
          }}
        >
          {supplier.name}
        </a>
      ),
      sorter: (a, b) => utils.antdTableSorter(a, b, "name"),
    },
    {
      title: t("email"),
      dataIndex: "email",
      render: (data) => <span>{data}</span>,
    },
    {
      title: t("products"),
      dataIndex: "productIds",
      render: (productIds) => <span>{productIds && productIds.length > 0 ? productIds.length : 0}</span>,
      sorter: (a, b) => {
        const aCount = a.productIds?.length || 0;
        const bCount = b.productIds?.length || 0;
        return aCount - bCount;
      },
    },
    {
      title: t("currency"),
      dataIndex: "currency",
      render: (currency) => <Tag>{currency || "USD"}</Tag>,
      sorter: (a, b) => (a.currency || "USD").localeCompare(b.currency || "USD"),
    },
    {
      title: t("status"),
      dataIndex: "status",
      render: (status) => <Tag color={getSupplierStatusColor(status)}>{status && t(`supplier.${status}`)}</Tag>,
      sorter: (a, b) => utils.antdTableSorter(a, b, "status"),
    },
    {
      title: t("last_login"),
      dataIndex: "lastLogin",
      render: (data) => <span>{data ? dayjs.unix(data).format("DD-MM-YYYY HH:mm") : "-"}</span>,
      sorter: (a, b) => utils.antdTableSorter(a, b, "createdAt"),
      defaultSortOrder: "descend",
    },
    {
      title: "",
      dataIndex: "actions",
      render: (_, supplier) => (
        <div className="text-right">
          <EllipsisDropdown
            menu={
              <Menu>
                <Menu.Item onClick={() => openEditModal(supplier)}>
                  <Flex alignItems="center">
                    <EditOutlined />
                    <span style={{ marginLeft: 4 }}>{t("edit")}</span>
                  </Flex>
                </Menu.Item>
                <Menu.Item onClick={() => showDrawer(supplier)}>
                  <Flex alignItems="center">
                    <EyeOutlined />
                    <span style={{ marginLeft: 4 }}>{t("view")}</span>
                  </Flex>
                </Menu.Item>
                <Menu.Item onClick={() => handleDeleteSupplier(supplier)} danger>
                  <Flex alignItems="center">
                    <DeleteOutlined />
                    <span style={{ marginLeft: 4 }}>{t("delete")}</span>
                  </Flex>
                </Menu.Item>
              </Menu>
            }
          />
        </div>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <h2>{t("supplier.suppliers")}</h2>
      <Card>
        {isMobile ? (
          <>
            <div style={{ marginBottom: 16 }}>
              <Input
                value={searchText}
                placeholder={t("search")}
                prefix={<SearchOutlined />}
                onChange={(e) => onSearch(e.target.value)}
                style={{ marginBottom: 8 }}
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal} style={{ width: "100%" }}>
                {t("add_supplier")}
              </Button>
            </div>
            <div style={{ padding: "8px 0" }}>
              {filteredList && filteredList.length > 0 ? (
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  {filteredList.map((supplier) => (
                    <SupplierCard key={supplier.id} supplier={supplier} />
                  ))}
                </Space>
              ) : (
                <Card>
                  <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>{t("no_data")}</div>
                </Card>
              )}
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <Input
                value={searchText}
                placeholder={t("search")}
                prefix={<SearchOutlined />}
                onChange={(e) => onSearch(e.target.value)}
                style={{ maxWidth: 300 }}
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
                {t("add_supplier")}
              </Button>
            </div>
            <div className="table-responsive">
              <Table
                columns={tableColumns}
                dataSource={filteredList}
                rowKey="id"
                showSorterTooltip={false}
                loading={loading}
                scroll={{ x: 800 }}
              />
            </div>
          </>
        )}
      </Card>

      {/* Add Modal */}
      <Modal
        title={t("add_supplier")}
        open={addModalVisible}
        onCancel={closeAddModal}
        onOk={handleAddSupplier}
        okText={t("save")}
        cancelText={t("close")}
        confirmLoading={submitLoading}
        width={isMobile ? "100%" : 1000}
        styles={{ body: { padding: isMobile ? 12 : 24 } }}
      >
        <Form form={addForm} layout="vertical">
          <Form.Item name="exactSupplierId" label="Exact Leverancier">
            <Select
              placeholder="Selecteer Exact leverancier"
              showSearch
              allowClear
              loading={exactSuppliersLoading}
              optionFilterProp="label"
              onChange={async (value) => {
                if (value) {
                  const selectedExactSupplier = exactSuppliers.find((supplier) => supplier.Id === value);
                  if (selectedExactSupplier) {
                    addForm.setFieldsValue({
                      name: selectedExactSupplier.Name || "",
                      email: selectedExactSupplier.Email || "",
                    });

                    // Fetch supplier items and auto-select matching products
                    try {
                      const token = await getFirebaseIDToken();
                      const response = await axios.get(`${getExactSupplierItems}${value}/items`, {
                        headers: { Authorization: token },
                      });

                      if (response.data && Array.isArray(response.data)) {
                        // Extract item codes from the response
                        const itemCodes = response.data.map((item) => item.ItemCode?.trim()).filter(Boolean);

                        // Find matching products by code
                        const matchingProductIds =
                          products
                            ?.filter((product) => product.code && itemCodes.includes(product.code.trim()))
                            .map((product) => product.id) || [];

                        // Set the matching products in the form
                        if (matchingProductIds.length > 0) {
                          addForm.setFieldsValue({
                            productIds: matchingProductIds,
                          });
                          setAddModalProductIds(matchingProductIds);
                        }
                      }
                    } catch (error) {
                      console.error("Error fetching supplier items:", error);
                      // Don't show error to user, just log it
                    }
                  }
                } else {
                  // Clear products when exact supplier is cleared
                  addForm.setFieldsValue({
                    productIds: [],
                  });
                  setAddModalProductIds([]);
                }
              }}
              options={exactSuppliers.map((supplier) => ({
                value: supplier.Id,
                label: `${supplier.Name}${supplier.Code ? ` (${supplier.Code.trim()})` : ""}`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="name"
            label={t("name")}
            rules={[
              {
                required: true,
                message: t("form.enter_value"),
              },
            ]}
          >
            <Input placeholder={t("name")} />
          </Form.Item>
          <Form.Item
            name="email"
            label={t("email")}
            rules={[
              {
                required: true,
                message: t("form.enter_email"),
              },
              {
                type: "email",
                message: t("form.enter_valid_email"),
              },
            ]}
          >
            <Input placeholder={t("email")} />
          </Form.Item>
          <Form.Item
            name="currency"
            label={t("currency")}
            initialValue="USD"
            rules={[
              {
                required: true,
                message: t("form.select_value"),
              },
            ]}
          >
            <Select
              placeholder={t("select_currency")}
              options={[
                { value: "USD", label: "USD - US Dollar" },
                { value: "IDR", label: "IDR - Indonesian Rupiah" },
              ]}
            />
          </Form.Item>
          <Form.Item name="productIds" label={t("products")}>
            <Transfer
              dataSource={getTransferDataSource()}
              showSearch
              filterOption={(inputValue, item) =>
                item.title.toLowerCase().includes(inputValue.toLowerCase()) ||
                item.code.toLowerCase().includes(inputValue.toLowerCase())
              }
              listStyle={{
                width: "100%",
                height: 400,
              }}
              targetKeys={addModalProductIds}
              onChange={(targetKeys) => {
                setAddModalProductIds(targetKeys);
                addForm.setFieldsValue({ productIds: targetKeys });
              }}
              render={renderTransferItem}
              titles={[t("available_products"), t("selected_products")]}
              locale={{
                itemUnit: t("product"),
                itemsUnit: t("products"),
                searchPlaceholder: t("search"),
              }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        title={t("edit_supplier")}
        open={editModalVisible}
        onCancel={closeEditModal}
        onOk={handleEditSupplier}
        okText={t("save")}
        cancelText={t("close")}
        confirmLoading={submitLoading}
        width={isMobile ? "100%" : 1000}
        styles={{ body: { padding: isMobile ? 12 : 24 } }}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="name"
            label={t("name")}
            rules={[
              {
                required: true,
                message: t("form.enter_value"),
              },
            ]}
          >
            <Input placeholder={t("name")} />
          </Form.Item>
          <Form.Item
            name="status"
            label={t("status")}
            rules={[
              {
                required: true,
                message: t("form.select_value"),
              },
            ]}
          >
            <Select
              placeholder={t("select_status")}
              options={[
                { value: SUPPLIER_STATUS_CREATED, label: t("supplier.created") },
                { value: SUPPLIER_STATUS_INVITED, label: t("supplier.invited") },
                { value: SUPPLIER_STATUS_ACTIVE, label: t("supplier.active") },
                { value: SUPPLIER_STATUS_INACTIVE, label: t("supplier.inactive") },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="currency"
            label={t("currency")}
            rules={[
              {
                required: true,
                message: t("form.select_value"),
              },
            ]}
          >
            <Select
              placeholder={t("select_currency")}
              options={[
                { value: "USD", label: "USD - US Dollar" },
                { value: "IDR", label: "IDR - Indonesian Rupiah" },
              ]}
            />
          </Form.Item>
          <Form.Item name="productIds" label={t("products")}>
            <Transfer
              dataSource={getTransferDataSource()}
              showSearch
              filterOption={(inputValue, item) =>
                item.title.toLowerCase().includes(inputValue.toLowerCase()) ||
                item.code.toLowerCase().includes(inputValue.toLowerCase())
              }
              listStyle={{
                width: "100%",
                height: 400,
              }}
              targetKeys={editModalProductIds}
              onChange={(targetKeys) => {
                setEditModalProductIds(targetKeys);
                editForm.setFieldsValue({ productIds: targetKeys });
              }}
              render={renderTransferItem}
              titles={[t("available_products"), t("selected_products")]}
              locale={{
                itemUnit: t("product"),
                itemsUnit: t("products"),
                searchPlaceholder: t("search"),
              }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Drawer */}
      <Drawer
        title={selectedSupplier?.name}
        placement="right"
        onClose={closeDrawer}
        open={drawerVisible}
        width={isMobile ? window.innerWidth : 720}
        styles={{ body: { padding: isMobile ? 12 : 24 } }}
        extra={
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => {
              closeDrawer();
              openEditModal(selectedSupplier);
            }}
          >
            {t("edit")}
          </Button>
        }
      >
        {selectedSupplier && (
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label={t("name")}>{selectedSupplier.name}</Descriptions.Item>
            <Descriptions.Item label={t("email")}>{selectedSupplier.email}</Descriptions.Item>
            <Descriptions.Item label={t("currency")}>
              <Tag>{selectedSupplier.currency || "USD"}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t("status")}>
              <Tag color={getSupplierStatusColor(selectedSupplier.status)}>
                {t(`supplier.${selectedSupplier.status}`)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t("created_at")}>
              {selectedSupplier.createdAt ? dayjs.unix(selectedSupplier.createdAt).format("DD-MM-YYYY HH:mm") : "-"}
            </Descriptions.Item>
            {selectedSupplier.updatedAt && (
              <Descriptions.Item label={t("updated_at")}>
                {dayjs.unix(selectedSupplier.updatedAt).format("DD-MM-YYYY HH:mm")}
              </Descriptions.Item>
            )}
            {selectedSupplier.lastLogin && (
              <Descriptions.Item label={t("last_login")}>
                {dayjs.unix(selectedSupplier.lastLogin).format("DD-MM-YYYY HH:mm")}
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t("products")}>
              {selectedSupplier.productIds && selectedSupplier.productIds.length > 0 ? (
                <div>
                  {selectedSupplier.productIds.map((productId) => {
                    const product = products?.find((p) => p.id === productId);
                    return product ? (
                      <Tag key={productId} style={{ marginBottom: 4 }}>
                        {product.name} {product.code ? `(${product.code})` : ""}
                      </Tag>
                    ) : null;
                  })}
                </div>
              ) : (
                <span>-</span>
              )}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </>
  );
};

const MobileSupplierCard = styled(Card)`
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  margin-bottom: 4px;

  .ant-card-body {
    padding: 12px;
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export default Suppliers;
